<template>
  <div class="media-wrapper">
    <!-- Toggle button -->
    <button
      class="toggle-overlay-btn"
      :style="{ right: overlayVisible ? '260px' : '10px' }"
      @click="toggleOverlay"
    >
      <span v-if="overlayVisible">⮜</span>
      <span v-else>⮞</span>
    </button>

    <!-- Main focus view -->
    <div class="primary-view">
      <!-- Show avatar for audio calls -->
      <img
        v-if="focusedItem.type === 'audio'"
        :src="focusedItem.avatar"
        class="avatar"
        alt="Audio Avatar"
      />
      <audio
        v-else-if="focusedItem.type === 'audio'"
        :src="focusedItem.src"
        ref="focusedAudio"
        class="focused-audio"
      ></audio>
      <!-- Webpage view -->
      <iframe
        v-else-if="focusedItem.type === 'webpage'"
        :src="focusedItem.src"
        sandbox="allow-same-origin allow-scripts allow-popups allow-forms"
        allowfullscreen
      ></iframe>
      <!-- Video or stream view -->
      <video
        v-else-if="focusedItem.type === 'videoFile' || focusedItem.type === 'stream'"
        :src="focusedItem.src"
        controls
        autoplay
      ></video>
    </div>

    <!-- Overlay menu -->
    <div class="video-grid-overlay" :class="{ collapsed: !overlayVisible }">
      <!-- Expanded tiles -->
      <div v-if="overlayVisible">
        <div
          v-for="(item, i) in mediaItems"
          :key="i"
          class="video-tile"
          ref="tiles"
          v-bind="draggables[i]"
        >
          <!-- Tile content -->
          <img
            v-if="item.type === 'audio'"
            :src="item.avatar || 'https://via.placeholder.com/150/007BFF/ffffff?text=Audio'"
            class="tile-avatar"
            alt="Avatar"
          />
          <audio
                v-if="item.type === 'audio'"
                :src="item.src"
                ref="tileAudios"
                class="tile-audio"
            ></audio>
          <iframe
            v-if="item.type === 'webpage'"
            :src="item.src"
            sandbox="allow-same-origin allow-scripts allow-popups allow-forms"
            allowfullscreen
          ></iframe>
          <video
            v-else
            :src="item.src"
            muted
          ></video>

          <!-- Transparent overlay to capture focus click -->
          <div class="tile-overlay" @click="focusItem(item)"></div>

          <!-- Icon bar for non-webpage -->
          <div v-if="item.type !== 'webpage'" class="icon-bar">
            <button :class="{ active: isMuted[i] }" @click.stop="toggleMute(i)">
              <i :class="['fal', isMuted[i] ? 'fa-microphone-slash' : 'fa-microphone']"></i>
            </button>
            <button :class="{ active: isPlaying[i] }" @click.stop="togglePlay(i)">
              <i :class="['fal', isPlaying[i] ? 'fa-pause' : 'fa-play']"></i>
            </button>
            <button @click.stop="toggleFullscreenTile(i)">
              <i class="fal fa-expand"></i>
            </button>
            <div class="dropdown">
              <button class="dropdown-toggle" @click.stop="toggleDropdown(i)">
                <i class="fal fa-ellipsis-h"></i>
              </button>
              <ul v-if="dropdownOpen[i]" class="dropdown-menu">
                <li @click.stop="option1(i)">Option 1</li>
                <li @click.stop="option2(i)">Option 2</li>
              </ul>
            </div>
          </div>
        </div>
      </div>
      <!-- Collapsed initials view -->
      <div v-else>
        <div
          v-for="(item, i) in mediaItems"
          :key="i"
          class="initial-tile"
          @click="focusItem(item); toggleOverlay()"
        >
          {{ getInitials(item.src) }}
        </div>
      </div>
    </div>

    <!-- Global controls (hidden) -->
    <div class="global-controls" style="display:none;">
      <button @click="reloadAll">Reload All</button>
    </div>

      <!-- Bottom persistent control bar -->
    <div class="bottom-bar">
      <button title="PSTN"
              class="control-button"
              :class="{active:startPstnFlag}"
              @click="startPstnFlag?handleStopPstn():handleStartPstn()"
      ><i :class="startPstnFlag?'far fa-phone':'far fa-phone-slash'"></i></button>

      <button title="Audio Call"
              class="control-button"
              :class="{active:startAudioFlag}"
              @click="startAudioFlag?handleStopAudio():handleStartAudio()"
      ><i :class="startAudioFlag?'far fa-phone-missed':'far fa-phone'"></i></button>

      <button title="Mute/Unmute"
              class="control-button"
              :class="{active:allMuted}"
              @click="allMuted?handleStopMute():handleStartMute()"
      ><i :class="allMuted?'far fa-microphone-slash':'far fa-microphone'"></i></button>

      <button title="Video"
              class="control-button"
              :class="{active:startVideoFlag}"
              @click="startVideoFlag?handleStopVideo():handleStartVideo()"
      ><i :class="startVideoFlag?'far fa-video-slash':'far fa-video'"></i></button>

      <button title="Screen Share"
              class="control-button"
              :class="{active:startScreenFlag}"
              @click="startScreenFlag?handleStopScreen():handleStartScreen()"
      ><i class="far fa-share-square"></i></button>

      <button title="Whiteboard"
              class="control-button"
              :class="{active:startWhiteboardFlag}"
              @click="startWhiteboardFlag?handleStopWhiteboard():handleStartWhiteboard()"
      ><i class="far fa-edit"></i></button>

      <button title="Recording"
              class="control-button"
              :class="{active:startRecordingFlag}"
              @click="startRecordingFlag?handleStopRecording():handleStartRecording()"
      ><i :class="startRecordingFlag?'far fa-dot-circle':'far fa-stop-circle'"></i></button>

      <button title="Chat"
              class="control-button"
              :class="{active:startChatFlag==='meeting'}"
              @click="startChatFlag==='meeting'?handleStopChat():handleStartChat()"
      ><i :class="startChatFlag==='meeting'?'far fa-comment-alt':'far fa-comment'"></i></button>

      <button title="Share"
              class="control-button"
              @click="copyToClipboard()"
      ><i class="far fa-share-alt"></i></button>

      <button v-if="startMeetingFlag"
              title="Disconnect"
              class="control-button active"
              @click="handleStopMeeting()"
      ><i class="far fa-phone-slash" style="color:red"></i></button>

      <button title="More"
              class="control-button more-button"
              @click="toggleBottomMenu()"
      ><i class="far fa-ellipsis-h"></i></button>

      <div v-if="bottomMenuOpen" class="bottom-dropdown-menu">
        <button class="menu-item" @click="handleRaiseHand()">
          <i class="far fa-hand-paper"></i> Raise Hand
        </button>
        <button class="menu-item" @click="handleAddParticipant()">
          <i class="far fa-user-plus"></i> Add Participant
        </button>
      </div>
    </div>

  </div>
</template>

<script>
import { ref, onMounted } from 'vue';
import { useDraggable } from '@vueuse/core';


export default {
  name: 'MediaGrid',
  setup() {
    
    const inputs = [
      { src: 'https://debianlargeserver-0050-kamalk-0054.laxroute53.com/direct-to-waiter', type: 'webpage' },
      { src: 'https://www.w3schools.com/html/movie.mp4', type: 'videoFile' },
      { src: 'https://interactive-examples.mdn.mozilla.net/media/cc0-videos/flower.mp4', type: 'stream' },
      { src: 'https://www.soundhelix.com/examples/mp3/SoundHelix-Song-1.mp3', type: 'audio', avatar: 'https://avatar.iran.liara.run/public' }
    ];

    const mediaItems = ref(inputs);
    const focusedItem = ref(inputs[0]);
    const overlayVisible = ref(false);
    const isMuted = ref(inputs.map(() => false));
    const isPlaying = ref(inputs.map(() => false));
    const dropdownOpen = ref(inputs.map(() => false));
    const tiles = ref([]);
    const draggables = ref([]);
    // bottom bar flags
    const startPstnFlag = ref(false);
    const startAudioFlag = ref(false);
    const allMuted = ref(false);
    const startVideoFlag = ref(false);
    const startScreenFlag = ref(false);
    const startWhiteboardFlag = ref(false);
    const startRecordingFlag = ref(false);
    const startChatFlag = ref('');
    const startMeetingFlag = ref(false);
    const bottomMenuOpen = ref(false);

   // console.log("got client id for SmartMeetingComponent",props.clientID,"=========",props.viewportID);

    onMounted(() => {
      draggables.value = tiles.value.map(el => useDraggable(el));
      tiles.value.forEach((tile, i) => {
        const video = tile.querySelector('video');
        const audio = tile.querySelector('audio.tile-audio');
        if (video) {
          video.onplay = () => (isPlaying.value[i] = true);
          video.onpause = () => (isPlaying.value[i] = false);
        }
        if (audio) {
          audio.onplay = () => (isPlaying.value[i] = true);
          audio.onpause = () => (isPlaying.value[i] = false);
        }
      });
    });

    const focusItem = item => (focusedItem.value = item);
    const toggleOverlay = () => (overlayVisible.value = !overlayVisible.value);
    const getInitials = src =>
      src
        ? new URL(src).host.replace('www.', '').split('.').map(p => p[0].toUpperCase()).join('')
        : '';

    // handlers
    const handleStartPstn = () => {
      
      startPstnFlag.value = true;
      console.log("handle start Pstn");

      let callerAudioFlag = false, callerVideoFlag = false, callerScreenFlag = false;
      let params = {"ClientID":client.Client,"ToPE":null,"mediaType":"Audio"};
      
      if(params.mediaType == "Screen") {
        callerScreenFlag = true;
      } else if(params.mediaType == "Video") {
        callerVideoFlag = true;
      }
      let CallPE = params.ToPE;
      let InitialContext = {Caller:{Audio:callerAudioFlag,NoShow:true,Screen:callerScreenFlag,Video:callerVideoFlag,Pstn:true},Callee:{Audio:true,NoShow:true,Screen:false,Video:false},Calltype:"JOINCALL",Label:"Invite"};
      let callObj = {Mode:"STARTMEDIA" ,ClientID:params.ClientID,DialTimeout:60,CallPE:CallPE,InitialContext:InitialContext,isSignedIn: false};
      window.top.BotServices.startCall(callObj);

    }

    const handleStopPstn = () => {
      startPstnFlag.value = false;
      console.log("handle stop Pstn");
      window.top.BotServices.stopMedia({ClientID : client.Client, MediaType : 'Pstn' });
   
    }

    const handleStartAudio = () => {
      startAudioFlag.value = true;
      let CallObj = {"ClientID":client.Client,"ToPE":'',"mediaType":"Audio"};
      window.top.DevMgr.startMedia(viewportid,client.Client,CallObj);
    }

    const handleStopAudio = () => {
      startAudioFlag.value = false; 
      window.top.BotServices.stopMedia({ClientID : client.Client, MediaType : 'Audio' });
    }

    const handleStartMute = () => {
      allMuted.value = true;
    }

    const handleStopMute = () => {
      allMuted.value = false;
    }

    const handleStartVideo = () => {
      startVideoFlag.value = true;
    }

    const handleStopVideo = () => {
      startVideoFlag.value = false;
    }

    const handleStartScreen = () => {
      startScreenFlag.value = true;
      console.log("handle start screen");
      let CallObj = {"ClientID":client.Client,"ToPE":'',"mediaType":"Screen"};
      window.top.DevMgr.startMedia(viewportid,client.Client,CallObj);

    }

    const handleStopScreen = () => {
      startScreenFlag.value = false;
      console.log("handle stop screen");
      window.top.BotServices.stopMedia({ClientID : client.Client, MediaType : 'Screen' });
  
    }

    const handleStartWhiteboard = () => {
      startWhiteboardFlag.value = true;
      console.log("handle start whiteboard");
      let terminationUrl = {"MediaType":"Iframe","URL":"/WhiteBoard-0052/tldraw/index.html?meetingId="+MeetingContext.MeetingParentId};
      window.top.BotServices.openChildPanel({ClientID : client.Client, DisplayName : 'Whiteboard', Termination : terminationUrl, "Template":"oxygen-whiteboard",CssLink:"/WhiteBoard-0052/tldraw/static/css/main.e6c13ad2.css"}, ()=>{});
    }

    const handleStopWhiteboard = () => {
      startWhiteboardFlag.value = false;
      console.log("handle stop whiteboard");
      window.top.BotServices.stopMedia({ClientID : client.Client, MediaType : 'Data' });
    }
    
    const handleStartRecording = () => {
      startRecordingFlag.value = true;
    }

    const handleStopRecording = () => {
      startRecordingFlag.value = false;
    }

    const handleStartChat = () => {
      startChatFlag.value = 'meeting';
    }

    const handleStopChat = () => {
      startChatFlag.value = '';
    }

    const handleStopMeeting = () => {
      startMeetingFlag.value = false;
    }

    const copyToClipboard = () => {};
    const handleRaiseHand = () => {
      console.log("Raise hand clicked");
      window.top.BotServices.callManagerApi({action: 'raiseHand', data : { MeetingContext, currentContext }});
    };

    const handleAddParticipant = () => {};
    const toggleBottomMenu = () => bottomMenuOpen.value = !bottomMenuOpen.value;

    const reloadAll = () => {
      tiles.value.forEach((tile, i) => {
        const item = mediaItems.value[i];
        const el = tile.querySelector(item.type === 'webpage' ? 'iframe' : 'video');
        if (item.type === 'webpage') el.src = el.src;
        else { el.currentTime = 0; el.play(); }
      });
    };

    const toggleMute = i => {
      const video = tiles.value[i].querySelector('video');
      const audio = tiles.value[i].querySelector('audio.tile-audio');
      if (video) {
        video.muted = !video.muted;
        isMuted.value[i] = video.muted;
      } else if (audio) {
        audio.muted = !audio.muted;
        isMuted.value[i] = audio.muted;
      }
    };

    const togglePlay = i => {
      const video = tiles.value[i].querySelector('video');
      const audio = tiles.value[i].querySelector('audio.tile-audio');
      if (video) {
        video.paused ? video.play() : video.pause();
      } else if (audio) {
        audio.paused ? audio.play() : audio.pause();
      }
    };

    const toggleFullscreenTile = i => {
      const el = tiles.value[i];
      !document.fullscreenElement ? el.requestFullscreen() : document.exitFullscreen();
    };

    const toggleDropdown = i => {
      dropdownOpen.value[i] = !dropdownOpen.value[i];
    };
    const option1 = i => (dropdownOpen.value[i] = false);
    const option2 = i => (dropdownOpen.value[i] = false);

    return {
      mediaItems,
      focusedItem,
      overlayVisible,
      isMuted,
      isPlaying,
      dropdownOpen,
      startPstnFlag,
      startAudioFlag,
      allMuted,
      startVideoFlag,
      startScreenFlag,
      startWhiteboardFlag,
      startRecordingFlag,
      startChatFlag,
      startMeetingFlag,
      bottomMenuOpen,
      tiles,
      draggables,
      focusItem,
      toggleOverlay,
      getInitials,
      reloadAll,
      toggleMute,
      togglePlay,
      toggleFullscreenTile,
      toggleDropdown,
      option1,
      option2,
      handleStartPstn,
      handleStopPstn,
      handleStartAudio,
      handleStopAudio,
      handleStartMute,
      handleStopMute,
      handleStartVideo,
      handleStopVideo,
      handleStartScreen,
      handleStopScreen,
      handleStartWhiteboard,
      handleStopWhiteboard,
      handleStartRecording,
      handleStopRecording,
      handleStartChat,
      handleStopChat,
      handleStopMeeting,
      copyToClipboard,
      handleRaiseHand,
      handleAddParticipant,
      toggleBottomMenu
    };
  }
};
</script>

<style scoped>
.media-wrapper { display: flex; flex-direction: column; height: 100vh; background: white; position: relative; }
.toggle-overlay-btn { position: absolute; top: 36px; z-index: 4; background: #333; color: #fff; border: none; border-radius: 4px; padding: 6px 10px; cursor: pointer; transition: right 0.3s; }
.primary-view { flex: 1; position: relative; background: #eee; overflow: hidden; display: flex; justify-content: center; align-items: center; }
.primary-view iframe, .primary-view video { width: 100%; height: 100%; object-fit: contain; border: none; }
.primary-view .avatar { width: 150px; height: 150px; border-radius: 50%; object-fit: cover; }
.video-grid-overlay { position: absolute; top: 0; right: 0; width: 250px; height: 100%; background: rgba(255,255,255,0.95); display: flex; flex-direction: column; overflow-y: auto; padding: 10px; box-shadow: -2px 0 8px rgba(0,0,0,0.1); z-index: 2; transition: width 0.3s ease, padding 0.3s ease; }
.video-grid-overlay.collapsed { width: 40px; padding: 5px; }
.video-tile { position: relative; width: 100%; height: 120px; margin-bottom: 10px; border: 1px solid #ccc; border-radius: 8px; overflow: hidden; background: #fff; cursor: pointer; transition: transform 0.2s; }
.video-tile:hover { transform: scale(1.03); }
.video-tile iframe, .video-tile video, .tile-avatar { width: 100%; height: 100%; object-fit: cover; }
.tile-overlay { position: absolute; top: 0; left: 0; width: 100%; height: 100%; cursor: pointer; z-index: 3; }
.icon-bar { position: absolute; bottom: 0; left: 0; width: 100%; display: flex; justify-content: space-around; background: rgba(0,0,0,0.6); padding: 5px 0; border-top: 1px solid rgba(255,255,255,0.2); z-index: 4; }
.icon-bar button { background: none; border: none; color: white; font-size: 18px; cursor: pointer; }
.icon-bar button.active i { color: red; }
.dropdown { position: relative; }
.dropdown-menu { position: absolute; top: 24px; right: 0; background: #fff; border: 1px solid #ccc; border-radius: 4px; list-style: none; padding: 5px 0; margin: 0; box-shadow: 0 2px 8px rgba(0,0,0,0.15); z-index: 5; }
.dropdown-menu li { padding: 5px 12px; cursor: pointer; }
.dropdown-menu li:hover { background: #f0f0f0; }
.initial-tile { display: flex; align-items: center; justify-content: center; width: 100%; height: 40px; margin-bottom: 5px; border: 1px solid #ccc; border-radius: 8px; background: #f7f7f7; color: #333; font-weight: bold; cursor: pointer; }
.global-controls { position: absolute; bottom: 10px; left: 50%; transform: translateX(-50%); background: #f0f0f0; padding: 8px 12px; border-radius: 6px; display: flex; gap: 10px; z-index: 3; }
.global-controls button { padding: 6px 12px; background: #333; color: #fff; border: none; border-radius: 4px; cursor: pointer; }
.bottom-bar { 
    position: fixed;
    bottom: 0;
    left: 0;
    width: 100%;
    display: flex;
    justify-content: center;
    align-items: center;
    padding: 10px 0px;
    background-color: white;
    border-top: 1px solid lightgray;
    box-shadow: 0 -2px 4px rgba(0, 0, 0, 0.1);
    z-index: 100;
}
.control-button { background: none;
    border: none;
    font-size: 20px;
    cursor: pointer;
    align-items: center;
    background-color: #fff;
    border: none;
    border-radius: 50%;
    box-shadow: 0 2px 4px rgba(0, 0, 0, .1);
    color: #333;
    cursor: pointer;
    display: flex;
    height: 31px;
    justify-content: center;
    transition: all .3s ease;
    margin: 0 4px;
    width: 31px;
  }
.control-button.active i { color: red; }
.more-button { }
.bottom-dropdown-menu { position: absolute; bottom: 48px; background: #fff; border: 1px solid #ccc; border-radius: 4px; box-shadow: 0 2px 8px rgba(0,0,0,0.15); }
.bottom-dropdown-menu .menu-item { display: flex; align-items: center; gap: 8px; padding: 8px 12px; background: none; border: none; cursor: pointer; width: 100%; text-align: left; }
.bottom-dropdown-menu .menu-item:hover { background: #f0f0f0; }

</style>
